import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText, Printer, Copy, ChevronDown, Check, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { copyToClipboard } from '@/utils/clipboard';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';

/**
 * ExportMenu - Universal export system for all reports
 * Supports Excel, PDF, CSV, Print, and Clipboard
 */
const ExportMenu = ({
  data = [],
  columns = [],
  filename = 'report',
  reportTitle = 'Report',
  theme = 'light',
  disabled = false,
  onExportStart = null,
  onExportComplete = null
}) => {
  const isDark = theme === 'dark';
  const [isOpen, setIsOpen] = useState(false);
  const [exporting, setExporting] = useState(null);

  // Get visible columns only
  const visibleColumns = columns.filter(col => col.visible !== false);

  // Format value for export
  const formatExportValue = (value, column) => {
    if (value === null || value === undefined) return '';
    
    switch (column.dataType) {
      case 'currency':
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      case 'number':
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      case 'date':
        if (typeof value === 'string') return value;
        return new Date(value).toLocaleDateString('en-IN');
      default:
        return String(value);
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    setExporting('excel');
    onExportStart?.('excel');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 100)); // Allow UI to update
      
      const worksheetData = data.map(row => {
        const rowData = {};
        visibleColumns.forEach(col => {
          rowData[col.header] = formatExportValue(row[col.field], col);
        });
        return rowData;
      });

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      
      // Set column widths
      worksheet['!cols'] = visibleColumns.map(col => ({ wch: Math.max(col.header.length, 15) }));
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, reportTitle.substring(0, 31));
      
      XLSX.writeFile(workbook, `${filename}.xlsx`);
      onExportComplete?.('excel', true);
    } catch (error) {
      console.error('Excel export error:', error);
      onExportComplete?.('excel', false, error);
    } finally {
      setExporting(null);
      setIsOpen(false);
    }
  };

  // Export to CSV
  const exportToCSV = async () => {
    setExporting('csv');
    onExportStart?.('csv');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const headers = visibleColumns.map(col => `"${col.header}"`).join(',');
      const rows = data.map(row => 
        visibleColumns.map(col => {
          const value = formatExportValue(row[col.field], col);
          return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',')
      );
      
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `${filename}.csv`);
      
      onExportComplete?.('csv', true);
    } catch (error) {
      console.error('CSV export error:', error);
      onExportComplete?.('csv', false, error);
    } finally {
      setExporting(null);
      setIsOpen(false);
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    setExporting('pdf');
    onExportStart?.('pdf');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      
      // Title
      doc.setFontSize(16);
      doc.text(reportTitle, 14, 15);
      
      // Date
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, 22);
      
      // Table
      const tableColumns = visibleColumns.map(col => ({
        header: col.header,
        dataKey: col.field
      }));
      
      const tableData = data.map(row => {
        const rowData = {};
        visibleColumns.forEach(col => {
          rowData[col.field] = formatExportValue(row[col.field], col);
        });
        return rowData;
      });

      doc.autoTable({
        columns: tableColumns,
        body: tableData,
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { top: 28 }
      });
      
      // Page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 25, doc.internal.pageSize.height - 10);
      }
      
      doc.save(`${filename}.pdf`);
      onExportComplete?.('pdf', true);
    } catch (error) {
      console.error('PDF export error:', error);
      onExportComplete?.('pdf', false, error);
    } finally {
      setExporting(null);
      setIsOpen(false);
    }
  };

  // Print
  const handlePrint = async () => {
    setExporting('print');
    onExportStart?.('print');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { font-size: 18px; margin-bottom: 5px; }
            .meta { font-size: 12px; color: #666; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th { background: #3b82f6; color: white; padding: 8px 6px; text-align: left; }
            td { padding: 6px; border-bottom: 1px solid #e5e7eb; }
            tr:nth-child(even) { background: #f9fafb; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>${reportTitle}</h1>
          <div class="meta">Generated: ${new Date().toLocaleString('en-IN')} | Total Records: ${data.length}</div>
          <table>
            <thead>
              <tr>${visibleColumns.map(col => `<th>${col.header}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${data.map(row => `
                <tr>${visibleColumns.map(col => `<td>${formatExportValue(row[col.field], col)}</td>`).join('')}</tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
      
      onExportComplete?.('print', true);
    } catch (error) {
      console.error('Print error:', error);
      onExportComplete?.('print', false, error);
    } finally {
      setExporting(null);
      setIsOpen(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    setExporting('copy');
    onExportStart?.('copy');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const headers = visibleColumns.map(col => col.header).join('\t');
      const rows = data.map(row => 
        visibleColumns.map(col => formatExportValue(row[col.field], col)).join('\t')
      );
      
      const text = [headers, ...rows].join('\n');
      const ok = await copyToClipboard(text);
      if (!ok) window.prompt('Copy the report below (Ctrl+C):', text);
      
      onExportComplete?.('copy', ok);
    } catch (error) {
      console.error('Copy error:', error);
      onExportComplete?.('copy', false, error);
    } finally {
      setExporting(null);
      setIsOpen(false);
    }
  };

  const exportOptions = [
    { id: 'excel', label: 'Excel (.xlsx)', icon: FileSpreadsheet, action: exportToExcel, color: 'text-green-500' },
    { id: 'csv', label: 'CSV', icon: FileText, action: exportToCSV, color: 'text-blue-500' },
    { id: 'pdf', label: 'PDF', icon: FileText, action: exportToPDF, color: 'text-red-500' },
    { id: 'print', label: 'Print', icon: Printer, action: handlePrint, color: 'text-purple-500' },
    { id: 'copy', label: 'Copy to Clipboard', icon: Copy, action: copyToClipboard, color: 'text-amber-500' },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || data.length === 0}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          disabled || data.length === 0
            ? 'opacity-50 cursor-not-allowed'
            : isDark 
              ? 'bg-white/5 text-white hover:bg-white/10' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
        data-testid="export-menu-btn"
      >
        <Download size={16} />
        <span>Export</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)} 
          />
          
          {/* Dropdown */}
          <div 
            className={`absolute right-0 top-full mt-1 w-52 rounded-lg border shadow-xl z-50 py-1 ${
              isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-white border-gray-200'
            }`}
            data-testid="export-menu-dropdown"
          >
            {exportOptions.map((option) => (
              <button
                key={option.id}
                onClick={option.action}
                disabled={exporting !== null}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isDark ? 'hover:bg-white/5 text-slate-300' : 'hover:bg-gray-50 text-gray-700'
                } ${exporting === option.id ? 'opacity-70' : ''}`}
              >
                {exporting === option.id ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <option.icon size={16} className={option.color} />
                )}
                <span className="flex-1 text-left">{option.label}</span>
                {exporting === option.id && (
                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Exporting...</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ExportMenu;
